import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import logo from "../logo.png";
import { API_ENDPOINTS } from "../config/constants";
import { isPOSLocked, POS_LOCK_EVENT, removePOSLockPin, setPOSLocked, verifyPOSLockPin } from "../utils/posLock";

export default function POSLockOverlay({ user, onLogout }) {
  const restaurantId = user?.id || user?.restaurant_id;
  const restaurantName = user?.restaurant_name || user?.shopname || user?.restaurantname || "Restaurant";
  const userName = user?.name || user?.owner_name || "User";
  const [locked, setLocked] = useState(() => isPOSLocked(restaurantId));
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [recovering, setRecovering] = useState(false);
  const [mobile, setMobile] = useState(user?.mobile_number || user?.mobile || "");
  const [password, setPassword] = useState("");
  const [checking, setChecking] = useState(false);
  const attemptKey = `pos_lock_failed_attempts_${restaurantId || "unknown"}`;
  const [failedAttempts, setFailedAttempts] = useState(() => Number(localStorage.getItem(attemptKey) || 0));

  const clearFailedAttempts = () => {
    localStorage.removeItem(attemptKey);
    setFailedAttempts(0);
  };

  useEffect(() => {
    const sync = () => setLocked(isPOSLocked(restaurantId));
    sync();
    window.addEventListener(POS_LOCK_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => { window.removeEventListener(POS_LOCK_EVENT, sync); window.removeEventListener("storage", sync); };
  }, [restaurantId]);

  const unlockWithPin = async () => {
    setError("");
    if (!await verifyPOSLockPin(restaurantId, pin)) {
      const nextAttempts = failedAttempts + 1;
      if (nextAttempts >= 4) {
        clearFailedAttempts();
        setPin("");
        onLogout();
        return;
      }
      localStorage.setItem(attemptKey, String(nextAttempts));
      setFailedAttempts(nextAttempts);
      setError(nextAttempts === 3
        ? "Warning: This is your final attempt. One more incorrect PIN will log you out automatically."
        : `Incorrect PIN. ${4 - nextAttempts} attempts remaining.`);
      setPin("");
      return;
    }
    clearFailedAttempts();
    setPOSLocked(restaurantId, false);
    setPin("");
  };

  useEffect(() => {
    if (!locked || recovering) return undefined;
    const onKeyDown = (event) => {
      if (/^\d$/.test(event.key)) {
        event.preventDefault();
        setPin((current) => `${current}${event.key}`.slice(0, 6));
        setError("");
      } else if (event.key === "Backspace") {
        event.preventDefault();
        setPin((current) => current.slice(0, -1));
      } else if (event.key === "Enter" && pin.length >= 4) {
        event.preventDefault();
        void unlockWithPin();
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  });

  const recover = async () => {
    if (!mobile.trim() || !password) return setError("Enter restaurant phone number and password.");
    setChecking(true);
    setError("");
    try {
      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile_number: mobile.trim(), password }),
      });
      const json = await response.json().catch(() => ({}));
      const verifiedUser = json?.data || {};
      if (!response.ok || String(verifiedUser?.id || verifiedUser?.restaurant_id || "") !== String(restaurantId)) {
        throw new Error(response.ok ? "These credentials belong to another restaurant." : (json?.message || "Incorrect phone number or password."));
      }
      removePOSLockPin(restaurantId);
      clearFailedAttempts();
      setPassword("");
    } catch (reason) {
      setError(reason?.message || "Unable to verify credentials.");
    } finally { setChecking(false); }
  };

  if (!locked) return null;
  const addDigit = (digit) => { setPin((current) => `${current}${digit}`.slice(0, 6)); setError(""); };

  return <Overlay role="dialog" aria-modal="true" aria-label="POS locked">
    <Curtain $side="left" /><Curtain $side="right" />
    <CenterStage>
      <BrandLogo src={logo} alt="Bill Sarthi" />
      <LockLabel><span>●</span> POS LOCKED</LockLabel>
      <RestaurantName>{restaurantName}</RestaurantName>
      <UserName>Signed in as <strong>{userName}</strong></UserName>
      {!recovering ? <LockPanel>
        <PinDots aria-label={`${pin.length} PIN digits entered`}>{[0, 1, 2, 3, 4, 5].map((index) => <i key={index} className={index < pin.length ? "filled" : ""} />)}</PinDots>
        <Instruction>Enter your PIN to unlock</Instruction>
        <Keypad>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => <Key type="button" key={digit} onClick={() => addDigit(String(digit))}>{digit}</Key>)}
          <Key type="button" className="utility" onClick={() => { setPin(""); setError(""); }}>C</Key>
          <Key type="button" onClick={() => addDigit("0")}>0</Key>
          <Key type="button" className="utility" onClick={() => setPin((current) => current.slice(0, -1))}>⌫</Key>
        </Keypad>
        {error && <ErrorText>{error}</ErrorText>}
        <UnlockButton type="button" onClick={() => void unlockWithPin()} disabled={pin.length < 4}>Unlock POS</UnlockButton>
        <LinkButton type="button" onClick={() => { setRecovering(true); setError(""); }}>Forgot PIN?</LinkButton>
      </LockPanel> : <RecoveryPanel>
        <RecoveryIcon>🔑</RecoveryIcon><h3>Recover Access</h3>
        <p>Verify this restaurant’s phone number and account password. Your old PIN will be removed.</p>
        <Field type="tel" inputMode="numeric" placeholder="Restaurant phone number" value={mobile} onChange={(event) => setMobile(event.target.value.replace(/\D/g, ""))} />
        <Field type="password" placeholder="Account password" value={password} onChange={(event) => setPassword(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void recover(); }} />
        {error && <ErrorText>{error}</ErrorText>}
        <UnlockButton type="button" onClick={() => void recover()} disabled={checking}>{checking ? "Verifying…" : "Verify & Unlock"}</UnlockButton>
        <LinkButton type="button" onClick={() => { setRecovering(false); setError(""); }}>← Back to PIN</LinkButton>
      </RecoveryPanel>}
      <LogoutButton type="button" onClick={onLogout}>⏻ Logout</LogoutButton>
      <FooterText>Powered by Bill Sarthi</FooterText>
    </CenterStage>
  </Overlay>;
}

const curtainLeft = keyframes`from{transform:translateX(-101%)}to{transform:translateX(0)}`;
const curtainRight = keyframes`from{transform:translateX(101%)}to{transform:translateX(0)}`;
const reveal = keyframes`0%,58%{opacity:0;transform:translateY(14px) scale(.97)}100%{opacity:1;transform:none}`;
const pulse = keyframes`0%,100%{box-shadow:0 0 0 0 rgba(125,211,252,.3)}50%{box-shadow:0 0 0 8px rgba(125,211,252,0)}`;
const Overlay = styled.div`position:fixed;inset:0;z-index:200000;overflow:auto;display:grid;place-items:center;padding:20px 14px;background:var(--app-primary);color:#fff;`;
const Curtain = styled.div`position:fixed;top:0;bottom:0;width:50.5%;z-index:1;pointer-events:none;${({$side})=>$side === "left" ? "left:0;" : "right:0;"}background:linear-gradient(90deg,rgba(0,35,70,.26) 0%,transparent 18%,rgba(255,255,255,.08) 40%,transparent 62%,rgba(0,35,70,.26) 100%),repeating-linear-gradient(90deg,rgba(255,255,255,.045) 0 9%,rgba(0,0,0,.045) 9% 18%),var(--app-primary);animation:${({$side})=>$side === "left" ? curtainLeft : curtainRight} .82s cubic-bezier(.77,0,.18,1) both;`;
const CenterStage = styled.div`position:relative;z-index:2;width:min(390px,100%);text-align:center;animation:${reveal} 1.05s ease both;`;
const BrandLogo = styled.img`display:block;width:96px;height:96px;object-fit:cover;margin:0 auto 8px;border-radius:22px;filter:drop-shadow(0 12px 24px rgba(0,25,50,.26));`;
const LockLabel = styled.div`display:inline-flex;align-items:center;gap:7px;padding:6px 10px;border:1px solid rgba(255,255,255,.24);border-radius:999px;background:rgba(0,36,71,.25);font-size:10px;font-weight:900;letter-spacing:.14em;color:#dff5ff;span{color:#7dd3fc;animation:${pulse} 1.8s infinite;border-radius:50%}`;
const RestaurantName = styled.h1`margin:9px 0 3px;font-size:clamp(21px,4vw,28px);line-height:1.15;text-shadow:0 4px 14px rgba(0,28,56,.3);`;
const UserName = styled.div`font-size:12px;color:#cceaff;margin-bottom:13px;strong{color:#fff}`;
const LockPanel = styled.div`padding:15px 18px 12px;border:1px solid rgba(255,255,255,.2);border-radius:22px;background:rgba(0,42,78,.34);box-shadow:0 24px 60px rgba(0,28,55,.25),inset 0 1px 0 rgba(255,255,255,.08);`;
const PinDots = styled.div`display:flex;justify-content:center;gap:13px;height:18px;i{width:12px;height:12px;border:2px solid rgba(255,255,255,.62);border-radius:50%;transition:.16s}i.filled{background:#fff;border-color:#fff;transform:scale(1.08);box-shadow:0 0 12px rgba(255,255,255,.42)}}`;
const Instruction = styled.div`margin:4px 0 11px;color:#dff5ff;font-size:11px;font-weight:700;`;
const Keypad = styled.div`display:grid;grid-template-columns:repeat(3,1fr);gap:8px;width:min(285px,100%);margin:auto;`;
const Key = styled.button`height:46px;border:1px solid rgba(255,255,255,.23);border-radius:14px;background:rgba(255,255,255,.11);color:#fff;font-size:20px;font-weight:900;cursor:pointer;box-shadow:inset 0 1px 0 rgba(255,255,255,.1);transition:.12s;&:hover{background:rgba(255,255,255,.2);transform:translateY(-1px)}&:active{transform:scale(.96)}&.utility{font-size:14px;color:#cceaff}`;
const UnlockButton = styled.button`width:min(285px,100%);margin-top:11px;padding:11px;border:0;border-radius:13px;background:#fff;color:var(--app-primary);font-weight:900;cursor:pointer;box-shadow:0 9px 22px rgba(0,27,54,.2);&:disabled{opacity:.48;cursor:not-allowed}`;
const LinkButton = styled.button`display:block;margin:9px auto 0;border:0;background:transparent;color:#dff5ff;font-size:12px;font-weight:800;cursor:pointer;text-decoration:underline;text-underline-offset:3px;`;
const LogoutButton = styled.button`margin:10px auto 0;padding:7px 13px;border:1px solid rgba(255,255,255,.2);border-radius:10px;background:rgba(0,32,63,.2);color:#fff;font-size:12px;font-weight:800;cursor:pointer;`;
const FooterText = styled.div`margin-top:10px;color:rgba(255,255,255,.58);font-size:9px;letter-spacing:.1em;text-transform:uppercase;`;
const RecoveryPanel = styled(LockPanel)`h3{margin:5px 0;color:#fff}p{margin:0 0 11px;color:#cceaff;font-size:12px;line-height:1.45}`;
const RecoveryIcon = styled.div`font-size:27px`;
const Field = styled.input`width:min(285px,100%);box-sizing:border-box;margin:5px 0;padding:11px 12px;border:1px solid rgba(255,255,255,.32);border-radius:11px;background:rgba(255,255,255,.12);color:#fff;font-size:14px;outline:none;&::placeholder{color:#b9ddf1}&:focus{background:rgba(255,255,255,.18);border-color:#fff}`;
const ErrorText = styled.div`width:min(285px,100%);box-sizing:border-box;margin:8px auto 0;padding:7px 9px;border-radius:9px;background:rgba(127,29,29,.55);border:1px solid rgba(254,202,202,.34);color:#fee2e2;font-size:11px;font-weight:800;`;
